import angular from "angular";

import SubscriptionService from "./SubscriptionService";
import Assert from "tbAssert";

import TrackingTargetDataServiceModule from "../TrackingTargetDataService";

const mTrackingTargetDataSvc = new WeakMap();
const mQSvc = new WeakMap();
const mLogSvc = new WeakMap();

class TrackingTargetSubscriptionService extends SubscriptionService {
    constructor($q, $log, TrackingTargetData) {
        super();

        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert(TrackingTargetData != null, "Assertion Failure: Missing Tracking Target Data Service Dependency");

        mQSvc.set(this, $q);
        mLogSvc.set(this, $log);
        mTrackingTargetDataSvc.set(this, TrackingTargetData);
    }

    getTarget(trackingTargetId) {
        let targetRetrievalPromise =
            mTrackingTargetDataSvc.get(this)
                .get({targetId: trackingTargetId})
                .$promise
                .then(
                    (retrievedTarget) => {
                        return retrievedTarget.trackingTarget;
                    },
                    (errorResponse) => {
                        return mQSvc.get(this).reject(errorResponse);
                    }
                );

        return targetRetrievalPromise;
    }

    _getTargetSingleBatch (trackingTargetIds) {
        let trackingTargetIdsString = trackingTargetIds.join(",");
        let targetRetrievalPromise =
            mTrackingTargetDataSvc.get(this)
                .getBatch({
                    targetIds: trackingTargetIdsString
                })
                .$promise
                .then(
                    (retrievedTargets) => {
                        return retrievedTargets.trackingTargets;
                    },
                    (errorResponse) => {
                        return mQSvc.get(this).reject(errorResponse);
                    }
                );

        return targetRetrievalPromise;
    }

    getTargetBatch(trackingTargetIds) {
        const logSvc = mLogSvc.get(this);
        logSvc.debug("Starting Tracking Target Batch:");
        logSvc.debug(trackingTargetIds);

        trackingTargetIds.sort(function (ttIdX,ttIdY) {
            if ((!Number.isInteger(ttIdX)) || (!Number.isInteger(ttIdY))) {
                throw new TypeError("Invalid Tracking Target Id Type");
            }

            if (ttIdX < ttIdY) {
                return -1;
            }
            if (ttIdX > ttIdY) {
                return 1;
            }
            if (ttIdX === ttIdY) {
                return 0;
            }

            logSvc.error("Uncomparable Ids Found In Target Batch Preparation");
            logSvc.error(ttIdX);
            logSvc.error(ttIdY);
            throw new Error("Uncomparable Ids Found In Target Batch Preparation");
        });

        let trackingTargetIdBatches = [];
        let startIdx = 0;
        let numIds = trackingTargetIds.length;

        while (startIdx < numIds) {
            let trackingTargetIdBatch = trackingTargetIds.slice(startIdx, startIdx + 50);
            startIdx += trackingTargetIdBatch.length;
            trackingTargetIdBatches.push(trackingTargetIdBatch);
        }

        let batchRetrievalPromises = trackingTargetIdBatches.map((trackingTargetBatch) => {
            return this._getTargetSingleBatch(trackingTargetBatch);
        });

        let batchRetrievalPromise = mQSvc.get(this).all(batchRetrievalPromises);

        return batchRetrievalPromise.then(
            (singleBatchRetrievalResults) => {
                let batchRetrievalResults = [];
                singleBatchRetrievalResults.forEach((result) => {
                    logSvc.debug("Handling single batch With " + String(result.length + " results."));
                    Array.prototype.push.apply(batchRetrievalResults, result);
                });
                logSvc.debug("Returning " + String(batchRetrievalResults.length) + " batch results.");

                batchRetrievalResults.sort(function(resultX, resultY) {
                    let resultXId = resultX.trackingTargetId;
                    let resultYId = resultY.trackingTargetId;
                    if (angular.isUndefined(resultXId) || angular.isUndefined(resultYId)) {
                        throw new Error("At least one target in the batch returned an invalid result: missing target Id");
                    }

                    if (resultXId < resultYId) {
                        return -1;
                    }
                    if (resultXId > resultYId) {
                        return 1;
                    }
                    if (resultXId === resultYId) {
                        return 0;
                    }

                    logSvc.error("Uncomparable Results In Target Batch");
                    logSvc.error(resultX);
                    logSvc.error(resultY);
                    throw new Error("Uncomparable Ids In Target Batch");
                });

                let retrievedResultIds = batchRetrievalResults.map(
                    (result) => result.trackingTargetId
                );

                let compareResult = retrievedResultIds.every(
                    (resultId, resultIdIdx) => (resultId === trackingTargetIds[resultIdIdx])
                );

                logSvc.debug("Compare Result Is " + (compareResult ? "Ok." : "NOT Ok."));

                if (compareResult !== true) {
                    logSvc.error("Compare of tracking target batch results FAILED.");
                    logSvc.debug("TT Ids In");
                    logSvc.debug(trackingTargetIds);

                    logSvc.debug("TT Ids Out");
                    logSvc.debug(retrievedResultIds);
                }

                return batchRetrievalResults;
            },
            (errorResponses) => {
                let combinedErrorResponses = errorResponses.join(" ");
                return mQSvc.get(this).reject(combinedErrorResponses);
            }
        );

    }

    addSubscription(recipientId, trackingTargetId) {

        let subAddPromise =
            mTrackingTargetDataSvc.get(this)
                .addSubscription({
                    targetId: trackingTargetId,
                    recipientId: recipientId
                })
                .$promise
                .then(
                    (updatedTarget) => {
                        let updatedTgt = updatedTarget.trackingTarget;
                        let tgtSubs = updatedTgt.subscribers.length;
                        let foundSubscriber = false;
                        for (let tgtSubIdx = 0; (!foundSubscriber) && (tgtSubIdx < tgtSubs); tgtSubIdx++) {
                            if (updatedTgt.subscribers[tgtSubIdx].recipientId === recipientId) {
                                foundSubscriber = true;
                            }
                        }
                        if (!foundSubscriber) {
                            mQSvc.get(this).reject("Failed to add subscription.");
                        }
                        else {
                            return true;
                        }
                    },
                    (errorResponse) => {
                        return mQSvc.get(this).reject(errorResponse);
                    }
                );

        return subAddPromise;

    }

    removeSubscription(recipientId, trackingTargetId) {
        let subAddPromise =
            mTrackingTargetDataSvc.get(this)
                .removeSubscription({
                    targetId: trackingTargetId,
                    recipientId: recipientId
                })
                .$promise
                .then(
                    (updatedTarget) => {
                        let updatedTgt = updatedTarget.trackingTarget;
                        let tgtSubs = updatedTgt.subscribers.length;
                        let foundSubscriber = false;
                        for (let tgtSubIdx = 0; (!foundSubscriber) && (tgtSubIdx < tgtSubs); tgtSubIdx++) {
                            if (updatedTgt.subscribers[tgtSubIdx].recipientId === recipientId) {
                                foundSubscriber = true;
                            }
                        }
                        if (!foundSubscriber) {
                            return true;
                        }
                        else {
                            return mQSvc.get(this).reject("Failed to remove subscription.");
                        }
                    },
                    (errorResponse) => {
                        return mQSvc.get(this).reject(errorResponse);
                    }
                );

        return subAddPromise;
    }
}

TrackingTargetSubscriptionService.$inject = [ "$q", "$log", "TrackingTargetData" ];

export default angular
    .module("trackerbase-app.services.subscription-data-adapters.tracking-target-subscription-service",[
        TrackingTargetDataServiceModule
    ])
    .service("TrackingTargetSubscriptionData", TrackingTargetSubscriptionService)
    .name;




//////////////////
// WEBPACK FOOTER
// ./src/services/subscription-data-services/TrackingTargetSubscriptionService.js
// module id = JeHK
// module chunks = 1